import { useRef } from 'react';
import type { LastSuccessfulDataScope } from '../constant/lastSuccessfulDataScopes';

type LastSuccessfulDataBeneficiary = {
  beneficiaryTitleId?: string;
  serviceId?: string;
};

/**
 * Remembers the most recently defined `data` and returns it in place of a
 * `data` that has gone `undefined` on an error — the case TanStack's
 * `placeholderData: keepPreviousData` does NOT cover, since it only holds
 * the previous page's data while the new key is pending, not once the query
 * settles to `error`.
 *
 * Without this, a page>1 fetch that fails stampedes a paginated table back
 * to an empty/undefined state: rows disappear, `paginationMeta` goes
 * `undefined`, and Prev/Next stop working because there is nothing to page
 * off of. Feeding the last good `{ items, meta }` response back in keeps the
 * table navigable while the failed request's error still surfaces through
 * the normal channels (the global axios error interceptor / QueryCache
 * `onError`).
 *
 * @param data - The query's current `data` (whatever shape — pass the whole
 * paginated response object so items and meta stay in sync).
 * @param isError - The query's current `isError`.
 * @param scope - Which data source this is. Required; only values of
 * `LAST_SUCCESSFUL_DATA_SCOPES` type-check.
 * @param beneficiary - The current beneficiary (or `null` or `undefined`). The
 * fallback is only used for the same scope and the same
 * `beneficiaryTitleId` and `serviceId`, so a previous beneficiary's rows are
 * never shown after an error. The page number is not part of the key.
 * @returns `data` unchanged, except when `data` is `undefined` AND
 * `isError` is true, when it returns the last defined `data` seen for the
 * same scope and beneficiary, or `undefined` if there is none.
 */
export const useLastSuccessfulData = <T>(
  data: T | undefined,
  isError: boolean,
  scope: LastSuccessfulDataScope,
  beneficiary: LastSuccessfulDataBeneficiary | null | undefined,
): T | undefined => {
  const lastSuccessfulRef = useRef<{ data: T; key: string } | undefined>(
    undefined,
  );
  // Static part (which table/query) + dynamic part (which beneficiary and
  // service). The page number is deliberately not part of the key.
  const key = `${scope}:${beneficiary?.beneficiaryTitleId}:${beneficiary?.serviceId}`;

  if (data !== undefined) {
    lastSuccessfulRef.current = { data, key };
  }

  if (data === undefined && isError) {
    return lastSuccessfulRef.current?.key === key
      ? lastSuccessfulRef.current.data
      : undefined;
  }

  return data;
};
